import React, { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import {
  Calendar,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Download,
  GripVertical,
  LayoutGrid,
  Loader2,
  MoreHorizontal,
  PanelRightClose,
  Plus,
  Search,
  Send,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Badge } from 'components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet';
import { Skeleton } from 'components/ui/skeleton';
import {
  createTask,
  deleteTask,
  getAllTasks,
  updateTask,
} from '../services/taskManagementService';
import {
  createComment,
  deleteComment,
  getCommentsByTaskId,
} from '../services/taskCommentService';
import { readAuthSession } from '../services/authService';
import { getAllTeamMembers } from '../services/teamService';
import './TaskManagement.css';

const COLUMNS = [
  { id: 'todo', title: 'To Do', statusLabel: 'To Do', machine: 'TODO', accent: '#1e90ff' },
  { id: 'inProgress', title: 'In Progress', statusLabel: 'In Progress', machine: 'OVERDUE', accent: '#f59e0b' },
  { id: 'completed', title: 'Completed', statusLabel: 'Completed', machine: 'COMPLETED', accent: '#22c55e' },
];

function taskId(task) {
  return String(task.documentId || task.id);
}

function columnIdForTask(task) {
  if (task.currentStatus === 'Completed' || task.status === 'COMPLETED') return 'completed';
  if (task.currentStatus === 'In Progress' || task.status === 'OVERDUE') return 'inProgress';
  return 'todo';
}

function statusPayload(columnId) {
  const col = COLUMNS.find((c) => c.id === columnId);
  if (!col) return null;
  return { status: col.machine, currentStatus: col.statusLabel };
}

function convertDateToDisplay(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function priorityTone(priority) {
  if (priority?.includes('High')) return 'high';
  if (priority?.includes('Low')) return 'low';
  return 'medium';
}

function TaskCard({ task, isDragging, onOpen }) {
  const id = taskId(task);
  const columnId = columnIdForTask(task);
  const { attributes, listeners, setNodeRef: setDragRef, transform } = useDraggable({
    id,
    data: { task, columnId, type: 'task' },
  });
  const { setNodeRef: setDropRef, isOver: dropOver } = useDroppable({
    id: `drop-${id}`,
    data: { columnId, type: 'task-slot' },
  });

  const setRefs = (node) => {
    setDragRef(node);
    setDropRef(node);
  };

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  const tone = priorityTone(task.priority);
  const over = dropOver;

  return (
    <article
      ref={setRefs}
      style={style}
      className={`asana-card${isDragging ? ' asana-card--dragging' : ''}${over ? ' asana-card--over' : ''}`}
    >
      <div className="asana-card__top">
        <button
          type="button"
          className="asana-card__grip"
          aria-label="Drag task"
          {...listeners}
          {...attributes}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="asana-card__title-btn"
          onClick={() => onOpen(task)}
        >
          <span className="asana-card__title">{task.title}</span>
        </button>
      </div>

      <button type="button" className="asana-card__meta-btn" onClick={() => onOpen(task)}>
        <div className="asana-card__meta">
          {task.assignee ? (
            <span className="asana-card__assignee" title={task.assignee}>
              {task.assignee
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </span>
          ) : null}
          {task.dueDate ? (
            <span className="asana-card__due">
              <Calendar className="h-3.5 w-3.5" />
              {task.dueDate}
            </span>
          ) : null}
          <span className={`asana-card__priority asana-card__priority--${tone}`}>
            {tone}
          </span>
        </div>
      </button>
    </article>
  );
}

function BoardColumn({ column, tasks, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <section
      className={`asana-column${isOver ? ' asana-column--over' : ''}`}
      aria-label={column.title}
    >
      <header className="asana-column__header">
        <span
          className="asana-column__dot"
          style={{ background: column.accent }}
          aria-hidden
        />
        <h3 className="asana-column__title">{column.title}</h3>
        <span className="asana-column__count">{tasks.length}</span>
      </header>
      <div ref={setNodeRef} className="asana-column__list">
        {children}
        {tasks.length === 0 ? (
          <div className="asana-column__empty">Drop tasks here</div>
        ) : null}
      </div>
    </section>
  );
}

const SKELETON_CARD_COUNTS = [3, 2, 1];

function TaskCardSkeleton({ titleWidth = '72%' }) {
  return (
    <article className="asana-card task-skel-card" aria-hidden>
      <div className="asana-card__top">
        <Skeleton className="task-skel-grip h-4 w-3 shrink-0" />
        <div className="task-skel-title-wrap">
          <Skeleton className="h-4" style={{ width: titleWidth }} />
          <Skeleton className="mt-1.5 h-3 w-[55%]" />
        </div>
      </div>
      <div className="asana-card__meta task-skel-meta">
        <Skeleton className="h-6 w-6 rounded-full" />
        <Skeleton className="h-3.5 w-16 rounded" />
        <Skeleton className="ml-auto h-3.5 w-10 rounded" />
      </div>
    </article>
  );
}

function TaskManagementSkeleton() {
  return (
    <div className="task-skeleton" aria-busy="true" aria-label="Loading tasks">
      <section className="task-intro" aria-hidden>
        <div className="task-intro-text">
          <Skeleton className="h-7 w-28" />
          <Skeleton className="mt-2.5 h-4 w-72 max-w-full" />
        </div>
        <div className="task-intro-actions">
          <Skeleton className="h-9 w-[9.5rem] rounded-lg" />
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-9 w-[7.5rem] rounded-lg" />
        </div>
      </section>

      <div className="task-toolbar" aria-hidden>
        <Skeleton className="h-9 w-full max-w-md rounded-lg" />
        <div className="task-toolbar__spacer" />
        <div className="task-filters">
          <Skeleton className="h-9 w-[7.5rem] rounded-lg" />
          <Skeleton className="h-9 w-[8.5rem] rounded-lg" />
          <Skeleton className="h-9 w-[7.5rem] rounded-lg" />
        </div>
      </div>

      <div className="task-panel-body">
        <div className="asana-board">
          {COLUMNS.map((column, colIndex) => {
            const count = SKELETON_CARD_COUNTS[colIndex] ?? 2;
            const widths = ['78%', '64%', '70%'];
            return (
              <section
                key={column.id}
                className="asana-column asana-column--skeleton"
                aria-hidden
              >
                <header className="asana-column__header">
                  <span
                    className="asana-column__dot"
                    style={{ background: column.accent }}
                  />
                  <h3 className="asana-column__title">{column.title}</h3>
                  <Skeleton className="asana-column__count h-[1.35rem] w-[1.35rem] rounded-full" />
                </header>
                <div className="asana-column__list">
                  {Array.from({ length: count }, (_, i) => (
                    <TaskCardSkeleton
                      key={`${column.id}-skel-${i}`}
                      titleWidth={widths[i % widths.length]}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TaskManagement() {
  const [viewMode, setViewMode] = useState('board');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [priorityFilter, setPriorityFilter] = useState('All Priority');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [activeDragId, setActiveDragId] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'Medium Priority',
    category: 'Admin',
    dueDate: '',
    assignee: '',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAllTasks();
        if (!cancelled) setTasks(data);
      } catch (err) {
        console.error('Error loading tasks:', err);
        if (!cancelled) {
          if (err.code === 404 || err.message?.includes('not found') || err.message?.includes('Collection')) {
            setError('Task Management collection not found. Please run: npm run setup:task-management');
          } else {
            setError('Failed to load tasks. Please check your connection and try again.');
          }
          setTasks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTeamMembers(true);
      try {
        const members = await getAllTeamMembers();
        if (!cancelled) setTeamMembers(members);
      } catch (err) {
        console.error('Error loading team members:', err);
        if (!cancelled) setTeamMembers([]);
      } finally {
        if (!cancelled) setLoadingTeamMembers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filterTasks = (taskList) =>
    taskList.filter((task) => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase().trim();
        if (searchLower) {
          const searchableText = [
            task.title,
            task.description,
            task.assignee,
            task.category,
            task.priority,
            task.dueDate,
            task.currentStatus,
            task.status,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!searchableText.includes(searchLower)) return false;
        }
      }

      if (statusFilter !== 'All Status') {
        const taskStatus =
          task.status === 'COMPLETED'
            ? 'Completed'
            : task.status === 'OVERDUE'
              ? 'In Progress'
              : task.status === 'TODO'
                ? 'To Do'
                : task.currentStatus || 'In Progress';
        if (statusFilter !== taskStatus) return false;
      }

      if (categoryFilter !== 'All Categories' && task.category !== categoryFilter) return false;
      if (priorityFilter !== 'All Priority' && task.priority !== priorityFilter) return false;
      return true;
    });

  const boardColumns = useMemo(() => {
    const filtered = filterTasks(tasks);
    return {
      todo: filtered.filter((t) => columnIdForTask(t) === 'todo'),
      inProgress: filtered.filter((t) => columnIdForTask(t) === 'inProgress'),
      completed: filtered.filter((t) => columnIdForTask(t) === 'completed'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchTerm, statusFilter, categoryFilter, priorityFilter]);

  const teamTaskStats = useMemo(() => {
    const stats = {};
    teamMembers.forEach((member) => {
      stats[member.name] = {
        name: member.name,
        position: member.position,
        totalTasks: 0,
        todoTasks: 0,
        inProgressTasks: 0,
        completedTasks: 0,
        tasks: [],
      };
    });
    tasks.forEach((task) => {
      if (!task.assignee || !stats[task.assignee]) return;
      stats[task.assignee].totalTasks += 1;
      stats[task.assignee].tasks.push(task);
      const col = columnIdForTask(task);
      if (col === 'todo') stats[task.assignee].todoTasks += 1;
      else if (col === 'inProgress') stats[task.assignee].inProgressTasks += 1;
      else stats[task.assignee].completedTasks += 1;
    });
    return Object.values(stats);
  }, [tasks, teamMembers]);

  useEffect(() => {
    if (teamTaskStats.length === 0) {
      setSelectedPerson('');
      return;
    }
    const stillValid = teamTaskStats.some((m) => m.name === selectedPerson);
    if (!stillValid) {
      setSelectedPerson(teamTaskStats[0].name);
    }
  }, [teamTaskStats, selectedPerson]);

  const selectedMemberIndex = teamTaskStats.findIndex((m) => m.name === selectedPerson);
  const selectedMember =
    selectedMemberIndex >= 0 ? teamTaskStats[selectedMemberIndex] : null;

  const goToPrevPerson = () => {
    if (teamTaskStats.length === 0) return;
    const idx = selectedMemberIndex < 0 ? 0 : selectedMemberIndex;
    const next = (idx - 1 + teamTaskStats.length) % teamTaskStats.length;
    setSelectedPerson(teamTaskStats[next].name);
  };

  const goToNextPerson = () => {
    if (teamTaskStats.length === 0) return;
    const idx = selectedMemberIndex < 0 ? 0 : selectedMemberIndex;
    const next = (idx + 1) % teamTaskStats.length;
    setSelectedPerson(teamTaskStats[next].name);
  };

  const handleStatusChange = async (id, newStatusLabel) => {
    const col = COLUMNS.find((c) => c.statusLabel === newStatusLabel);
    if (!col) return;
    const payload = statusPayload(col.id);
    const taskToUpdate = tasks.find((t) => taskId(t) === String(id));
    if (!taskToUpdate || !payload) return;

    try {
      const documentId = taskToUpdate.documentId || taskToUpdate.id;
      await updateTask(documentId, payload);
      setTasks((prev) =>
        prev.map((task) =>
          taskId(task) === String(documentId) ? { ...task, ...payload } : task
        )
      );
    } catch (err) {
      console.error('Error updating task status:', err);
      alert('Failed to update task status. Please try again.');
    }
  };

  const handleDeleteTask = async (task) => {
    if (columnIdForTask(task) !== 'todo') {
      alert('Tasks can only be deleted while they are in To Do.');
      return;
    }
    if (!window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) return;
    const id = taskId(task);
    try {
      await deleteTask(id);
      setTasks((prev) => prev.filter((t) => taskId(t) !== id));
      if (selectedTaskId === id) setSelectedTaskId(null);
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    }
  };

  const openTaskSheet = (task) => {
    setSelectedTaskId(taskId(task));
    setCommentDraft('');
  };

  const closeTaskSheet = () => {
    setSelectedTaskId(null);
    setComments([]);
    setCommentDraft('');
  };

  const selectedTask = selectedTaskId
    ? tasks.find((t) => taskId(t) === selectedTaskId) || null
    : null;

  const authSession = readAuthSession();

  useEffect(() => {
    if (!selectedTaskId) {
      setComments([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setCommentsLoading(true);
      try {
        const rows = await getCommentsByTaskId(selectedTaskId);
        if (!cancelled) setComments(rows);
      } catch (err) {
        console.error('Error loading comments:', err);
        if (!cancelled) setComments([]);
      } finally {
        if (!cancelled) setCommentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTaskId]);

  const handleSubmitComment = async () => {
    const text = commentDraft.trim();
    if (!text || !selectedTaskId || commentSaving) return;
    setCommentSaving(true);
    try {
      const created = await createComment({ taskId: selectedTaskId, body: text });
      setComments((prev) => [...prev, created]);
      setCommentDraft('');
    } catch (err) {
      console.error('Error posting comment:', err);
      alert(err.message || 'Failed to post comment. Please try again.');
    } finally {
      setCommentSaving(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    const session = readAuthSession();
    const isAuthor =
      session &&
      (comment.authorId === session.userId ||
        comment.authorId === session.email ||
        comment.authorEmail === session.email);
    if (!isAuthor) {
      alert('You can only delete your own comments.');
      return;
    }
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteComment(comment.id);
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const formatCommentTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const authorInitials = (name) =>
    String(name || '?')
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  const handleDragStart = (event) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = async (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const id = String(active.id);
    const fromColumn = active.data.current?.columnId;
    let toColumn = over.data.current?.columnId || over.id;

    if (!COLUMNS.some((c) => c.id === toColumn)) {
      const overTask = tasks.find((t) => taskId(t) === String(over.id).replace(/^drop-/, ''));
      toColumn = overTask ? columnIdForTask(overTask) : fromColumn;
    }

    if (!toColumn || toColumn === fromColumn) return;
    const col = COLUMNS.find((c) => c.id === toColumn);
    if (col) await handleStatusChange(id, col.statusLabel);
  };

  const handleOpenCreateModal = () => {
    setTaskForm({
      title: '',
      description: '',
      priority: 'Medium Priority',
      category: 'Admin',
      dueDate: '',
      assignee: '',
    });
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    if (creatingTask) return;
    setShowCreateModal(false);
  };

  const handleFormChange = (field, value) => {
    setTaskForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateTask = async () => {
    if (creatingTask) return;
    if (!taskForm.title || !taskForm.assignee) {
      alert('Please fill in Task Title and Assign To.');
      return;
    }
    setCreatingTask(true);
    try {
      const dueDateFormatted = taskForm.dueDate ? convertDateToDisplay(taskForm.dueDate) : '';
      const newTask = await createTask({
        title: taskForm.title,
        assignee: taskForm.assignee,
        dueDate: dueDateFormatted,
        status: 'TODO',
        priority: taskForm.priority,
        category: taskForm.category,
        description: taskForm.description,
        createdDate: new Date().toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }),
        currentStatus: 'To Do',
      });
      setTasks((prev) => [...prev, newTask]);
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating task:', err);
      alert('Failed to create task. Please try again.');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleExportToExcel = () => {
    try {
      const exportData = tasks.map((task) => ({
        'Task Title': task.title || '',
        Assignee: task.assignee || '',
        'Due Date': task.dueDate || '',
        Status: task.currentStatus || task.status || '',
        Priority: task.priority || '',
        Category: task.category || '',
        Description: task.description || '',
        'Created Date': task.createdDate || '',
      }));
      if (exportData.length === 0) {
        alert('No tasks to export.');
        return;
      }
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `Tasks_Export_${dateStr}.xlsx`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Failed to export tasks. Please try again.');
    }
  };

  const activeTask = activeDragId
    ? tasks.find((t) => taskId(t) === activeDragId)
    : null;

  const shell = (body) => (
    <div className="task-page">
      <div className="task-scroll">
        <div className="task-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Tasks' },
            ]}
          />
          {body}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return shell(<TaskManagementSkeleton />);
  }

  return shell(
    <>
      <section className="task-intro" aria-label="Tasks overview">
        <div className="task-intro-text">
          <h1 className="task-intro-title">Tasks</h1>
          <p className="task-intro-subtitle">
            Board view across To Do, In Progress, and Completed
          </p>
        </div>
        <div className="task-intro-actions">
          <div className="task-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`task-view-toggle__btn${viewMode === 'board' ? ' is-active' : ''}`}
              onClick={() => setViewMode('board')}
            >
              <LayoutGrid className="h-4 w-4" />
              Board
            </button>
            <button
              type="button"
              className={`task-view-toggle__btn${viewMode === 'people' ? ' is-active' : ''}`}
              onClick={() => setViewMode('people')}
            >
              <Users className="h-4 w-4" />
              People
            </button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="task-soft-btn h-9 w-9"
            onClick={handleExportToExcel}
            title="Download Excel"
            aria-label="Download Excel"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button type="button" className="task-create-btn" onClick={handleOpenCreateModal}>
            <Plus className="h-4 w-4" />
            Create task
          </Button>
        </div>
      </section>

      {error ? (
        <div className="task-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="task-toolbar">
        <div className="task-search">
          <Search className="task-search__icon h-4 w-4" aria-hidden />
          <Input
            className="task-search__input"
            placeholder="Search tasks…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search tasks"
          />
        </div>
        <div className="task-toolbar__spacer" aria-hidden="true" />
        <div className="task-filters">
          <select
            className="task-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option>All Status</option>
            <option>To Do</option>
            <option>In Progress</option>
            <option>Completed</option>
          </select>
          <select
            className="task-filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option>All Categories</option>
            <option>Admin</option>
            <option>Operational</option>
          </select>
          <select
            className="task-filter-select"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option>All Priority</option>
            <option>High Priority</option>
            <option>Medium Priority</option>
            <option>Low Priority</option>
          </select>
        </div>
      </div>

      <div className="task-panel-body">
        {viewMode === 'people' ? (
          <div className="task-people">
            {teamTaskStats.length === 0 || !selectedMember ? (
              <p className="task-state">No team members found.</p>
            ) : (
              <>
                <div className="task-people__switcher">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="task-soft-btn h-9 w-9"
                    onClick={goToPrevPerson}
                    disabled={teamTaskStats.length < 2}
                    aria-label="Previous person"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <select
                    className="task-people__select"
                    value={selectedPerson}
                    onChange={(e) => setSelectedPerson(e.target.value)}
                    aria-label="Select person"
                  >
                    {teamTaskStats.map((member) => (
                      <option key={member.name} value={member.name}>
                        {member.name}
                        {member.totalTasks > 0 ? ` (${member.totalTasks})` : ''}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="task-soft-btn h-9 w-9"
                    onClick={goToNextPerson}
                    disabled={teamTaskStats.length < 2}
                    aria-label="Next person"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <span className="task-people__pager">
                    {selectedMemberIndex + 1} / {teamTaskStats.length}
                  </span>
                </div>

                {(() => {
                  const member = selectedMember;
                  const initials = member.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);
                  const pct =
                    member.totalTasks > 0
                      ? Math.round((member.completedTasks / member.totalTasks) * 100)
                      : 0;
                  return (
                    <div className="task-people__row">
                      <div className="task-people__head">
                        <span className="task-people__avatar">{initials}</span>
                        <div className="task-people__meta">
                          <h3>{member.name}</h3>
                          {member.position ? <p>{member.position}</p> : null}
                        </div>
                        <div className="task-people__stats">
                          <span>
                            <Circle className="h-3 w-3" /> {member.todoTasks}
                          </span>
                          <span>
                            <Loader2 className="h-3 w-3" /> {member.inProgressTasks}
                          </span>
                          <span>
                            <CheckCircle2 className="h-3 w-3" /> {member.completedTasks}
                          </span>
                          <span className="task-people__pct">{pct}%</span>
                        </div>
                      </div>
                      {member.tasks.length > 0 ? (
                        <ul className="task-people__tasks">
                          {member.tasks.map((task) => (
                            <li key={taskId(task)}>
                              <button
                                type="button"
                                className="task-people__task-btn"
                                onClick={() => openTaskSheet(task)}
                              >
                                <span className="task-people__task-title">{task.title}</span>
                                <Badge variant="secondary">
                                  {COLUMNS.find((c) => c.id === columnIdForTask(task))?.title}
                                </Badge>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="task-people__empty">No tasks assigned</p>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="asana-board">
              {COLUMNS.map((column) => {
                const colTasks = boardColumns[column.id] || [];
                return (
                  <BoardColumn key={column.id} column={column} tasks={colTasks}>
                    {colTasks.map((task) => (
                      <TaskCard
                        key={taskId(task)}
                        task={task}
                        isDragging={activeDragId === taskId(task)}
                        onOpen={openTaskSheet}
                      />
                    ))}
                  </BoardColumn>
                );
              })}
            </div>
            <DragOverlay>
              {activeTask ? (
                <div className="asana-card asana-card--overlay">
                  <span className="asana-card__title">{activeTask.title}</span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {showCreateModal ? (
        <div className="task-modal-overlay" onClick={handleCloseCreateModal}>
          <div
            className="task-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="task-modal__header">
              <h2 id="task-modal-title">Create task</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="task-soft-btn"
                onClick={handleCloseCreateModal}
                disabled={creatingTask}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="task-modal__form">
              <div className="task-field">
                <Label htmlFor="task-title">Title</Label>
                <Input
                  id="task-title"
                  value={taskForm.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder="Task title"
                  required
                />
              </div>
              <div className="task-field">
                <Label htmlFor="task-description">Description</Label>
                <textarea
                  id="task-description"
                  className="task-textarea"
                  value={taskForm.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={3}
                  placeholder="Optional details"
                />
              </div>
              <div className="task-field-row">
                <div className="task-field">
                  <Label htmlFor="task-priority">Priority</Label>
                  <select
                    id="task-priority"
                    className="task-filter-select"
                    value={taskForm.priority}
                    onChange={(e) => handleFormChange('priority', e.target.value)}
                  >
                    <option>Low Priority</option>
                    <option>Medium Priority</option>
                    <option>High Priority</option>
                  </select>
                </div>
                <div className="task-field">
                  <Label htmlFor="task-category">Category</Label>
                  <select
                    id="task-category"
                    className="task-filter-select"
                    value={taskForm.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                  >
                    <option>Admin</option>
                    <option>Operational</option>
                  </select>
                </div>
              </div>
              <div className="task-field">
                <Label htmlFor="task-due-date">Due date</Label>
                <Input
                  id="task-due-date"
                  type="date"
                  value={taskForm.dueDate}
                  onChange={(e) => handleFormChange('dueDate', e.target.value)}
                />
              </div>
              <div className="task-field">
                <Label htmlFor="task-assignee">Assign to</Label>
                <select
                  id="task-assignee"
                  className="task-filter-select"
                  value={taskForm.assignee}
                  onChange={(e) => handleFormChange('assignee', e.target.value)}
                  required
                  disabled={loadingTeamMembers}
                >
                  <option value="">
                    {loadingTeamMembers ? 'Loading…' : 'Select a team member'}
                  </option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.name}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="task-modal__actions">
                <Button
                  type="button"
                  variant="ghost"
                  className="task-soft-btn"
                  onClick={handleCloseCreateModal}
                  disabled={creatingTask}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCreateTask}
                  disabled={creatingTask}
                >
                  {creatingTask ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Create task
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <Sheet
        open={Boolean(selectedTask)}
        onOpenChange={(open) => {
          if (!open) closeTaskSheet();
        }}
      >
        <SheetContent side="right" className="task-detail-sheet">
          {selectedTask ? (
            <>
              <div className="task-detail-sheet__toolbar">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={`task-detail-complete${
                    columnIdForTask(selectedTask) === 'completed'
                      ? ' task-detail-complete--done'
                      : ''
                  }`}
                  onClick={() => {
                    const id = taskId(selectedTask);
                    if (columnIdForTask(selectedTask) === 'completed') {
                      handleStatusChange(id, 'To Do');
                    } else {
                      handleStatusChange(id, 'Completed');
                    }
                  }}
                >
                  <Check className="h-4 w-4" />
                  {columnIdForTask(selectedTask) === 'completed'
                    ? 'Completed'
                    : 'Mark complete'}
                </Button>

                <div className="task-detail-sheet__toolbar-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="task-soft-btn h-8 w-8"
                        aria-label="More actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {columnIdForTask(selectedTask) === 'todo' ? (
                        <>
                          <DropdownMenuItem
                            onClick={() => handleDeleteTask(selectedTask)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete task
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      ) : null}
                      <DropdownMenuItem onClick={closeTaskSheet}>
                        Close
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="task-soft-btn h-8 w-8"
                    onClick={closeTaskSheet}
                    aria-label="Close details"
                  >
                    <PanelRightClose className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="task-detail-sheet__scroll">
              <SheetHeader className="task-detail-sheet__header">
                <SheetTitle className="task-detail-sheet__title">
                  {selectedTask.title}
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Task details for {selectedTask.title}
                </SheetDescription>
              </SheetHeader>

              <div className="task-detail-sheet__props">
                <div className="task-detail-row">
                  <span className="task-detail-row__label">Assignee</span>
                  <div className="task-detail-row__value">
                    {selectedTask.assignee ? (
                      <span className="task-detail-pill">
                        <span className="task-detail-pill__avatar" aria-hidden>
                          {selectedTask.assignee
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                        </span>
                        {selectedTask.assignee}
                      </span>
                    ) : (
                      <span className="task-detail-row__empty">No assignee</span>
                    )}
                  </div>
                </div>

                <div className="task-detail-row">
                  <span className="task-detail-row__label">Due date</span>
                  <div className="task-detail-row__value">
                    {selectedTask.dueDate ? (
                      <span className="task-detail-pill task-detail-pill--due">
                        <Calendar className="h-3.5 w-3.5" />
                        {selectedTask.dueDate}
                      </span>
                    ) : (
                      <span className="task-detail-row__empty">No due date</span>
                    )}
                  </div>
                </div>

                <div className="task-detail-row">
                  <span className="task-detail-row__label">Status</span>
                  <div className="task-detail-row__value">
                    <select
                      className="task-detail-status"
                      value={
                        COLUMNS.find((c) => c.id === columnIdForTask(selectedTask))
                          ?.statusLabel || 'To Do'
                      }
                      onChange={(e) =>
                        handleStatusChange(taskId(selectedTask), e.target.value)
                      }
                    >
                      {COLUMNS.map((c) => (
                        <option key={c.id} value={c.statusLabel}>
                          {c.statusLabel}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="task-detail-row">
                  <span className="task-detail-row__label">Priority</span>
                  <div className="task-detail-row__value">
                    <span
                      className={`asana-card__priority asana-card__priority--${priorityTone(selectedTask.priority)}`}
                    >
                      {priorityTone(selectedTask.priority)}
                    </span>
                  </div>
                </div>

                <div className="task-detail-row">
                  <span className="task-detail-row__label">Category</span>
                  <div className="task-detail-row__value">
                    {selectedTask.category ? (
                      <span className="task-detail-pill task-detail-pill--category">
                        <span
                          className="task-detail-pill__swatch"
                          aria-hidden
                        />
                        {selectedTask.category}
                      </span>
                    ) : (
                      <span className="task-detail-row__empty">No category</span>
                    )}
                  </div>
                </div>
              </div>

              <section className="task-detail-section">
                <h3 className="task-detail-section__title">Description</h3>
                <p
                  className={`task-detail-section__body${
                    selectedTask.description ? '' : ' task-detail-section__body--empty'
                  }`}
                >
                  {selectedTask.description || 'What is this task about?'}
                </p>
              </section>

              <section className="task-detail-section task-detail-comments">
                <h3 className="task-detail-section__title">
                  Comments
                  {comments.length > 0 ? (
                    <span className="task-detail-comments__count">{comments.length}</span>
                  ) : null}
                </h3>

                <div className="task-detail-comments__list">
                  {commentsLoading ? (
                    <p className="task-detail-comments__empty">Loading comments…</p>
                  ) : comments.length === 0 ? (
                    <p className="task-detail-comments__empty">No comments yet</p>
                  ) : (
                    comments.map((comment) => {
                      const canDelete =
                        authSession &&
                        (comment.authorId === authSession.userId ||
                          comment.authorId === authSession.email ||
                          comment.authorEmail === authSession.email);
                      return (
                        <article key={comment.id} className="task-comment">
                          <span className="task-comment__avatar" aria-hidden>
                            {authorInitials(comment.authorName)}
                          </span>
                          <div className="task-comment__main">
                            <div className="task-comment__head">
                              <span className="task-comment__author">
                                {comment.authorName || 'User'}
                              </span>
                              <span className="task-comment__time">
                                {formatCommentTime(comment.createdAt)}
                              </span>
                              {canDelete ? (
                                <button
                                  type="button"
                                  className="task-comment__delete"
                                  onClick={() => handleDeleteComment(comment)}
                                  aria-label="Delete comment"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              ) : null}
                            </div>
                            <p className="task-comment__body">{comment.body}</p>
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
              </section>

              <div className="task-detail-sheet__meta">
                {selectedTask.createdDate
                  ? `Created ${selectedTask.createdDate}`
                  : null}
              </div>
              </div>

              <div className="task-detail-sheet__composer">
                <span className="task-comment__avatar" aria-hidden>
                  {authorInitials(authSession?.name || authSession?.email || '?')}
                </span>
                <div className="task-detail-sheet__composer-box">
                  <textarea
                    className="task-detail-sheet__composer-input"
                    rows={2}
                    placeholder="Add a comment…"
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitComment();
                      }
                    }}
                    disabled={commentSaving}
                    aria-label="Add a comment"
                  />
                  <div className="task-detail-sheet__composer-actions">
                    <Button
                      type="button"
                      size="icon"
                      className="task-detail-sheet__send"
                      disabled={!commentDraft.trim() || commentSaving}
                      onClick={handleSubmitComment}
                      aria-label="Send comment"
                      title="Send"
                    >
                      {commentSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

export default TaskManagement;
